// ═══════════════════════════════════════════════════════════════════════════════
// Admin dashboard — aggregate stats
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import type { AdminDashboardStats } from '@fdh/shared';
import { adminApi } from '../../lib/adminApi';
import { useLanguage } from '../../contexts/LanguageContext';

export const AdminDashboardPage = () => {
  const { t } = useLanguage();
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await adminApi.get<AdminDashboardStats>('/admin/dashboard');
      if (res.success && res.data) {
        setStats(res.data);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('auth.errorGeneric'));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <h1 className="text-2xl font-semibold text-white">{t('admin.nav.dashboard')}</h1>
      <p className="mt-1 text-sm text-slate-400">{t('admin.dashboardSubtitle')}</p>

      {error ? (
        <p className="mt-4 text-sm text-red-400" role="alert">
          {error}
        </p>
      ) : null}

      {stats ? (
        <dl className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(
            [
              ['admin.stat.totalUsers', stats.totalUsers],
              ['admin.stat.employers', stats.totalEmployers],
              ['admin.stat.helpers', stats.totalHelpers],
              ['admin.stat.pairings', stats.totalPairings],
              ['admin.stat.activePairings', stats.activePairings],
              ['admin.stat.todayActive', stats.todayActiveUsers],
              ['admin.stat.todayNew', stats.todayNewUsers],
              ['admin.stat.todayTasks', stats.todayTasks],
              ['admin.stat.todayReceipts', stats.todayReceipts],
            ] as const
          ).map(([key, value]) => (
            <div
              key={key}
              className="rounded-lg border border-slate-800 bg-slate-900/60 px-4 py-3"
            >
              <dt className="text-xs text-slate-500">{t(key)}</dt>
              <dd className="mt-1 text-2xl font-semibold text-white">{value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        !error && (
          <p className="mt-8 text-sm text-slate-500" role="status">
            {t('common.loading')}
          </p>
        )
      )}
    </div>
  );
};
